/**
 * What a shortened window would do to the dossiers already stored — before it
 * is done.
 *
 * This is the client-side twin of `app.retention.shorten_existing_windows`, and
 * it exists for one reason: shortening the window is destructive and fast. A
 * dossier already older than the new window comes out with a `delete_after` in
 * the past and the next sweep removes its PDFs, its extracted facts and every
 * run and report of it. The founder about to agree to that is entitled to the
 * numbers for *their* workspace, not a generic warning.
 *
 * The arithmetic mirrors the server's, rule for rule — if it drifts, the dialog
 * lies:
 *
 *  · the new date of a row is **its own** `created_at + retentionDays`, never
 *    `now + retentionDays`. Computing from now would hand every stored dossier
 *    a fresh lease and the dialog would report a re-dating that never happens;
 *  · a row moves **only earlier**: `candidate < delete_after`, strictly. A
 *    candidate landing exactly on the date the row already carries moves
 *    nothing, and a later one is discarded — lengthening is not retroactive;
 *  · a row with no `delete_after` carries no promise to shorten and is left
 *    alone (`app/retention.py` says the same, for the same reason);
 *  · "due" is the sweeper's own test, `delete_after <= now` — the boundary is
 *    inclusive there, so it is inclusive here.
 *
 * **One deliberate difference from the sweeper, and it is not an oversight.**
 * `dueImmediately` counts the dossiers *this change* makes due: a row already
 * past its window is due with or without the founder's decision, so counting it
 * would overstate what the click costs. It disappears at the next sweep either
 * way, and the dossier card already says so («Frist abgelaufen»).
 *
 * No endpoint behind any of this: `DossierRef` already carries `created_at` and
 * `delete_after`, and the workspace screen has the list loaded before the card
 * is painted. A server-side preview would be a second implementation of the
 * rule, which is the one thing this module must not become.
 */
import type { DossierRef } from '@/api/types'

const MS_PER_DAY = 24 * 60 * 60 * 1000

export interface RetentionImpact {
  /** Every dossier in the workspace — the denominator the reader compares to. */
  total: number
  /** Windows the change actually moves earlier. */
  redated: number
  /**
   * Of those, the ones the change makes due now: the dossiers that disappear at
   * the next sweep because of this decision. The number that matters.
   */
  dueImmediately: number
}

/**
 * Is `next` a shortening of `current`? The question the confirmation hangs on.
 *
 * Only a shortening reaches backwards, so only a shortening is worth asking
 * about; lengthening and a no-op destroy nothing and stay one click. A field
 * that is not a number at all (`NaN`) is not a shortening either — the browser's
 * own validation refuses it long before it could be saved.
 */
export function isShortening(current: number, next: number): boolean {
  return Number.isFinite(next) && next < current
}

/** How many stored dossiers a window of `retentionDays` would move, and kill. */
export function retentionImpact(
  dossiers: readonly DossierRef[],
  retentionDays: number,
  now: Date = new Date(),
): RetentionImpact {
  let redated = 0
  let dueImmediately = 0

  for (const dossier of dossiers) {
    if (!dossier.delete_after) continue
    const current = new Date(dossier.delete_after).getTime()
    const created = new Date(dossier.created_at).getTime()
    // A row whose dates do not parse is a broken row, not a promise to shorten.
    if (Number.isNaN(current) || Number.isNaN(created)) continue

    const candidate = created + retentionDays * MS_PER_DAY
    if (candidate >= current) continue
    redated += 1
    // `<=` is the sweeper's test. `current > now` keeps a row that was already
    // due out of the count: it goes at the next sweep with or without this.
    if (candidate <= now.getTime() && current > now.getTime()) dueImmediately += 1
  }

  return { total: dossiers.length, redated, dueImmediately }
}
